/**
 * 从 tuac_core.inf（与 tuac_core.js 同目录）读取 TUAC 参数与开关的缺省值。
 * 用于 MusicPlayer 未持久化时的初始值。
 */

const INF_URL = '/tuac/tuac_core.inf';

/** 解析 INI 格式：支持 [section]、key=value、key:value，忽略分号注释 */
function parseIni(text) {
  const sections = {};
  let current = null;
  const lines = (text || '').split(/\r?\n/);
  for (const line of lines) {
    const trimmed = line.replace(/#.*|;.*/, '').trim();
    if (!trimmed) continue;
    const sectionMatch = trimmed.match(/^\[([^\]]+)\]$/);
    if (sectionMatch) {
      current = sectionMatch[1].trim().toLowerCase();
      if (!sections[current]) sections[current] = {};
      continue;
    }
    if (current == null) continue;
    const sep = trimmed.includes('=') ? '=' : ':';
    const idx = trimmed.indexOf(sep);
    if (idx === -1) continue;
    const key = trimmed.slice(0, idx).trim();
    let value = trimmed.slice(idx + 1).trim();
    if (value.toLowerCase() === 'true') value = true;
    else if (value.toLowerCase() === 'false') value = false;
    else if (/^-?\d*\.?\d+$/.test(value)) value = Number(value);
    sections[current][key] = value;
  }
  return sections;
}

/** 从解析结果中取出 [tuac] 段，规范化为 TUAC 引擎参数（提交给 worklet / merge 增益） */
function getTuacSectionFromParsed(parsed) {
  const raw = parsed?.tuac || {};
  const bool = (v) => v === true || v === 'true' || v === '1';
  const num = (v, fallback) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };
  const num01 = (v, fallback) => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : fallback;
  };
  return {
    spatialDeconstruction: raw.spatialDeconstruction !== undefined ? bool(raw.spatialDeconstruction) : true,
    vocalFocus: num01(raw.vocalFocus, 0.15),
    elevation: num(raw.elevation, 15),
    distance: num(raw.distance, 1.8),
    roomDepth: num(raw.roomDepth, 1.5),
    humidity: num01(raw.humidity, 0.75),
    airHighPass: num01(raw.airHighPass, 1.0),
    smoothing: num01(raw.smoothing, 0.05),
    spatial: num01(raw.spatial, 0.35),
    fidelity: num01(raw.fidelity, 0.45),
    warmth: num01(raw.warmth, 0.3),
    thickness: num01(raw.thickness, 0.2),
    bassIntensity: num01(raw.bassIntensity, 0.5),
    inputGain: num01(raw.inputGain, 0.5),
    crossfeedEnabled: raw.crossfeedEnabled !== undefined ? bool(raw.crossfeedEnabled) : true,
    crossfeedUseDelay: raw.crossfeedUseDelay !== undefined ? bool(raw.crossfeedUseDelay) : true,
    crossfeedStrength: num(raw.crossfeedStrength, 60),
    outputGain: num(raw.outputGain, 60)
  };
}

/** 从解析结果中取出 [tuacgui] 并规范化为播放器使用的键与类型 */
function getDefaultsFromParsed(parsed) {
  const raw = parsed?.tuacgui || parsed?.tuac || {};
  const bool = (v) => v === true || v === 'true' || v === '1';
  const num = (v, fallback) => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : fallback;
  };
  const env = (v) => {
    const s = String(v).toLowerCase();
    return ['studio', 'plate', 'hall', 'ambience'].includes(s) ? s : 'studio';
  };
  return {
    surroundEnabled: raw.surroundEnabled !== undefined ? bool(raw.surroundEnabled) : true,
    surroundIntensity: num(raw.surroundIntensity ?? raw.spatial, 0.35),
    distortionEnabled: raw.distortionEnabled !== undefined ? bool(raw.distortionEnabled) : true,
    distortionIntensity: num(raw.distortionIntensity ?? raw.warmth, 0.3),
    bassLevel: num(raw.bassLevel ?? raw.bassIntensity, 0.5),
    reverbLevel: num(raw.reverbLevel, 0.05),
    environmentMode: env(raw.environmentMode),
    spaceModeEnabled: raw.spaceModeEnabled !== undefined ? bool(raw.spaceModeEnabled) : true,
    bassEnabled: raw.bassEnabled !== undefined ? bool(raw.bassEnabled) : true
  };
}

/** 硬编码 [tuac] 段缺省（网络失败或解析失败时使用） */
export function getHardcodedTuacParams() {
  return {
    spatialDeconstruction: true,
    vocalFocus: 0.15,
    elevation: 15,
    distance: 1.8,
    roomDepth: 1.5,
    humidity: 0.75,
    airHighPass: 1.0,
    smoothing: 0.05,
    spatial: 0.35,
    fidelity: 0.45,
    warmth: 0.3,
    thickness: 0.2,
    bassIntensity: 0.5,
    inputGain: 0.5,
    crossfeedEnabled: true,
    crossfeedUseDelay: true,
    crossfeedStrength: 60,
    outputGain: 60
  };
}

/** 硬编码缺省（网络失败或解析失败时使用） */
export function getHardcodedDefaults() {
  return {
    surroundEnabled: true,
    surroundIntensity: 0.35,
    distortionEnabled: true,
    distortionIntensity: 0.3,
    bassLevel: 0.5,
    reverbLevel: 0.05,
    environmentMode: 'studio',
    spaceModeEnabled: true,
    bassEnabled: true
  };
}

/** 解析 tuac_core.inf 文本，返回 { defaults, tuacParams } */
export function parseTuacInf(text) {
  const parsed = parseIni(text);
  return {
    defaults: getDefaultsFromParsed(parsed),
    tuacParams: getTuacSectionFromParsed(parsed)
  };
}

/** 请求 /tuac/tuac_core.inf，返回 { defaults, tuacParams }；失败时返回硬编码 */
export function fetchTuacDefaults() {
  return fetch(INF_URL, { cache: 'no-cache' })
    .then((r) => (r.ok ? r.text() : Promise.reject(new Error(r.statusText))))
    .then((text) => parseTuacInf(text))
    .catch(() => ({
      defaults: getHardcodedDefaults(),
      tuacParams: getHardcodedTuacParams()
    }));
}
